import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  EmailSequence,
  EmailSequenceOptions,
  EmailSequencePayload,
  EmailSequenceTemplate,
  EmailSequenceTemplatePayload,
} from "../features/work/pages/sequences/emailSequence.types";

export const emailSequencesSlice = createApi({
  reducerPath: "emailSequencesSlice",
  tagTypes: ["EmailSequence", "EmailSequenceTemplate"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getWorkSequences: build.query<EmailSequence[], { workId: string }>({
      query: ({ workId }) => ({
        url: "api/email-sequence/get-by-work",
        params: { workId },
      }),
      providesTags: ["EmailSequence"],
    }),
    getSequenceOptions: build.query<
      EmailSequenceOptions,
      {
        orgId: string;
        workId?: string;
        customerId?: string;
        serviceId?: string;
      }
    >({
      query: (params) => ({
        url: "api/email-sequence/options",
        params: Object.fromEntries(
          Object.entries(params).filter(([, value]) => !!value),
        ),
      }),
    }),
    startSequence: build.mutation<EmailSequence, EmailSequencePayload>({
      query: (body) => ({
        url: "api/email-sequence/start",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EmailSequence"],
    }),
    updateSequence: build.mutation<EmailSequence, EmailSequencePayload>({
      query: (body) => ({
        url: "api/email-sequence/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EmailSequence"],
    }),
    stopSequence: build.mutation<EmailSequence, { id: string; userId: string }>(
      {
        query: (params) => ({
          url: "api/email-sequence/stop",
          method: "PUT",
          params,
        }),
        invalidatesTags: ["EmailSequence"],
      },
    ),
    getSequenceTemplates: build.query<
      EmailSequenceTemplate[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: "api/email-sequence/templates",
        params: { orgId },
      }),
      providesTags: ["EmailSequenceTemplate"],
    }),
    saveSequenceTemplate: build.mutation<
      EmailSequenceTemplate,
      EmailSequenceTemplatePayload
    >({
      query: (body) => ({
        url: "api/email-sequence/templates",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EmailSequenceTemplate"],
    }),
    deleteSequenceTemplate: build.mutation<boolean, { id: string }>({
      query: (params) => ({
        url: "api/email-sequence/templates",
        method: "DELETE",
        params,
      }),
      invalidatesTags: ["EmailSequenceTemplate"],
    }),
  }),
});

export const {
  useGetWorkSequencesQuery,
  useGetSequenceOptionsQuery,
  useStartSequenceMutation,
  useUpdateSequenceMutation,
  useStopSequenceMutation,
  useGetSequenceTemplatesQuery,
  useSaveSequenceTemplateMutation,
  useDeleteSequenceTemplateMutation,
} = emailSequencesSlice;

/** The backend returns { message } for problems the user can fix. */
export const getSequenceErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { data?: unknown })?.data;
  if (data && typeof data === "object" && "message" in data) {
    return String((data as { message: string }).message);
  }
  return fallback;
};
